import {
  VideoTrack,
  useMaybeRoomContext,
  useParticipantInfo,
  type TrackReferenceOrPlaceholder,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { AnimatePresence, motion } from "motion/react";
import { Hand, MicOff } from "lucide-react";
import { parseHandRaised } from "../lib/handRaiseMetadata";
import { useLocalCameraAffordance } from "../hooks/useLocalCameraAffordance";
import { useTileReaction } from "../hooks/useTileReaction";
import { useCallLayoutTier } from "../hooks/useCallLayoutTier";
import { useParticipantRole } from "../hooks/useParticipantRole";
import { useTranslation } from "react-i18next";
import { DeviceAffordance } from "./DeviceAffordance";
import { RoleBadge } from "./RoleBadge";
import { ScreenShareViewer } from "./ScreenShareViewer";

interface CallTileProps {
  trackRef: TrackReferenceOrPlaceholder;
  /** When true, render the accent ring around the tile (active speaker). */
  isSpeaking?: boolean;
  className?: string;
  /** Tile size hint — affects avatar dimensions. */
  size?: "sm" | "md" | "lg";
  /** Suppress the bottom-left name pill. Used when an external pill is
   *  rendered on top of the tile instead. */
  hideNamePill?: boolean;
}

/**
 * Shared participant tile used by GridLayout, SpeakerLayout, and
 * SidebarLayout. Renders the camera stream when available,
 * an initials avatar otherwise. Overlays a name pill (with mic-muted glyph
 * when muted), a hand-raise badge, and a reaction badge when set.
 *
 * Reads camera/mic state from the track ref / participant directly, and the
 * active reaction via `useTileReaction` (context, see hooks/useTileReaction),
 * so we don't have to thread props through the layout components.
 */
export function CallTile({ trackRef, isSpeaking, className, size = "md", hideNamePill }: CallTileProps) {
  const { t } = useTranslation();
  const isMobile = useCallLayoutTier() === "mobile";
  const participant = trackRef.participant;
  // Subscribe to name/identity changes — `setName` after connect won't trigger
  // a tile re-render unless we read through useParticipantInfo.
  const { name, identity } = useParticipantInfo({ participant });
  const displayName = name?.trim() || prettyIdentity(identity ?? "");
  const pillName = participant.isLocal ? t("call.controls.you") : displayName;
  // GH-1935: the host — and only the host — sees which tiles are guests. Read
  // the local role through the optional room context so tiles rendered
  // outside a room (previews, tests) still work.
  const localRole = useParticipantRole(useMaybeRoomContext()?.localParticipant);
  const tileRole = useParticipantRole(participant);
  const showGuestBadge = !participant.isLocal && localRole === "admin" && tileRole === "guest";
  const initial = (displayName.trim()[0] ?? "?").toUpperCase();

  const cameraOn = !!(trackRef.publication && !trackRef.publication.isMuted);
  const micPub = participant.getTrackPublication(Track.Source.Microphone);
  const micOn = !!micPub && !micPub.isMuted;
  const handRaised = parseHandRaised(participant.metadata);
  const isTwin = tileRole === "twin";
  const activeReaction = useTileReaction(identity ?? participant.identity);
  // Screen-share is shown whole (object-contain → letterboxed) so nothing gets
  // cropped — `object-cover` would chop off the edges of the shared screen
  // (e.g. a bottom action bar). Camera tiles still fill the frame (cover).
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;
  // Mirror only the LOCAL participant's own camera (natural self-view, like a
  // mirror). Remote tiles render un-mirrored so others appear in their true
  // orientation. Never mirror screen-share (shared text/slides must read
  // correctly). This is LiveKit's default convention.
  const mirrorSelf = participant.isLocal && !isScreenShare;

  // GH-1179: when THIS is the local participant and their camera couldn't be
  // acquired, show the cause-specific affordance inside the placeholder so the
  // user understands why their video is off (only they can act on it).
  const localCameraCause = useLocalCameraAffordance();
  const showCameraAffordance =
    participant.isLocal && !isScreenShare && !cameraOn && !!localCameraCause;

  // "md" is 72px and "lg" 96px — the design's grid/main-tile Avatar sizes.
  const avatarSize =
    size === "sm" ? "size-10 text-base" : size === "lg" ? "size-24 text-3xl" : "size-18 text-2xl";

  return (
    <div
      className={`relative rounded-2xl overflow-hidden bg-white dark:bg-background border border-border ${
        isMobile || isSpeaking ? "ring-2 ring-primary" : ""
      } ${className ?? ""}`}
    >
      {isScreenShare && cameraOn ? (
        // Screen-share gets the zoom / pan / fullscreen wrapper (GH-1067).
        <ScreenShareViewer trackRef={trackRef} className="h-full w-full" />
      ) : cameraOn ? (
        <VideoTrack
          trackRef={trackRef}
          className={`h-full w-full ${isScreenShare ? "object-contain" : "object-cover"}${mirrorSelf ? " -scale-x-100" : ""}`}
        />
      ) : (
        <div className="h-full w-full flex flex-col items-center justify-center gap-2.5 px-3">
          <div
            className={`${avatarSize} rounded-full flex items-center justify-center font-bold bg-neutral-600 text-white select-none`}
          >
            {initial}
          </div>
          {showCameraAffordance && localCameraCause && (
            <DeviceAffordance
              kind="camera"
              cause={localCameraCause}
              className="max-w-full text-center"
            />
          )}
        </div>
      )}

      {!hideNamePill && (
        <div className="absolute bottom-3 start-3 flex items-center gap-2 px-3 py-1 bg-black/50 rounded-lg backdrop-blur-sm max-w-[calc(100%-1.5rem)]">
          <span className="text-white text-xs font-bold truncate max-w-[12rem]">{pillName}</span>
          {showGuestBadge && (
            <RoleBadge role="guest" className="border-white/40 text-white" />
          )}
          {!micOn && <MicOff className="size-3 text-red-400 shrink-0" />}
        </div>
      )}

      {isTwin && (
        <div className="absolute top-2 start-2 shadow">
          <RoleBadge role="twin" />
        </div>
      )}

      {!isTwin && handRaised && (
        <div
          aria-label={t("call.participants.handRaised")}
          className="absolute top-2 start-2 size-7 rounded-full flex items-center justify-center shadow bg-primary text-primary-foreground"
        >
          <Hand className="size-4" />
        </div>
      )}

      <AnimatePresence>
        {activeReaction && (
          <motion.div
            key={activeReaction.id}
            role="status"
            aria-label={`${pillName} reacted with ${activeReaction.emoji}`}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.4, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-2 end-2 size-8 rounded-full flex items-center justify-center shadow bg-white/95 dark:bg-neutral-900/90 text-lg leading-none"
          >
            {activeReaction.emoji}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function prettyIdentity(identity: string): string {
  return identity.split("@")[0];
}
